import {
  DEFAULT_SORT,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PER_PAGE,
  SYSTEM_KEYS,
} from "../constants/filter";
import { createSelector } from "@reduxjs/toolkit";
import { FilterStateObj } from "../slices/filter.slice";

const DEFAULT_OBJ = Object.freeze({}) as FilterStateObj;
const EMPTY_ARRAY = Object.freeze([]);

export const selectFilterState = (state: { filter: FilterStateObj }): FilterStateObj =>
  state.filter || DEFAULT_OBJ;

export const selectActiveColorOptionValueIds = createSelector([selectFilterState], (filter) => {
  const colors = filter?.color;
  if (!colors) return EMPTY_ARRAY as string[];
  return Array.isArray(colors) ? colors : [colors];
});

export const selectActiveSizeOptionValueIds = createSelector([selectFilterState], (filter) => {
  const sizes = filter?.size;
  if (!sizes) return EMPTY_ARRAY as string[];
  return Array.isArray(sizes) ? sizes : [sizes];
});

export const selectActiveDietaryRestrictionsIds = createSelector([selectFilterState], (filter) => {
  const dr = filter?.["diet"];
  if (!dr) return EMPTY_ARRAY as string[];
  return Array.isArray(dr) ? dr : [dr];
});

export const selectActiveBrandIds = createSelector([selectFilterState], (filter) => {
  const brands = filter?.brand;
  if (!brands) return EMPTY_ARRAY as number[];
  const brandsArr = Array.isArray(brands) ? brands : [brands];
  return brandsArr.flatMap((id: string) => String(id).split("-")).map(Number);
});

export const selectActivePriceRangeIds = createSelector([selectFilterState], (filter) => {
  const price = filter?.price;
  if (!price) return EMPTY_ARRAY;

  if (Array.isArray(price)) {
    return price;
  }
  if (typeof price === "string") {
    return [price];
  }
  if (typeof price === "object") {
    const { min, max } = price;
    if (typeof min === "undefined" || typeof max === "undefined") return EMPTY_ARRAY;
    return [`${min}_${max}`];
  }

  return EMPTY_ARRAY;
});

export const makeFilterSort = (defaultSort: string = DEFAULT_SORT) =>
  createSelector([selectFilterState], (filter) => filter.sort ?? defaultSort);

export const selectFilterCurrentPage = createSelector(
  [selectFilterState],
  (filter) => filter.currentPage ?? DEFAULT_CURRENT_PAGE,
);

export const selectFilterPerPage = createSelector(
  [selectFilterState],
  (filter) => filter.perPage ?? DEFAULT_PER_PAGE,
);

export const selectAppliedFiltersCount = createSelector([selectFilterState], (filterState) => {
  if (!filterState || typeof filterState !== "object") return 0;

  return Object.keys(filterState).reduce((count, key) => {
    if (SYSTEM_KEYS.has(key)) return count;
    const value = filterState[key as keyof FilterStateObj];
    if (value === null || value === undefined || value === "") return count;
    if (Array.isArray(value)) {
      return count + value.length;
    }
    if (typeof value === "object" && Object.keys(value).length === 0) return count;
    return count + 1;
  }, 0);
});

export const selectFilterActiveColorsCount = createSelector(
  [selectActiveColorOptionValueIds],
  (colors) => colors?.length || 0,
);
